// src/services/authService.ts
// 🔐 Centralized Authentication Service for Quartermaster SaaS
// Uses Amplify v6 fetchAuthSession() for fresh tokens on every request

import { fetchAuthSession, resetPassword, confirmResetPassword, updatePassword } from 'aws-amplify/auth';

// 📊 Centralized logging
import { logger } from '../utils/logger';

// Create service-specific logger
const authLogger = logger.createServiceLogger('AuthService');

export interface AuthToken {
  token: string;
  tenantId: string;
  userRole: string;
  userId: string;
  expiresAt: number;
}

export interface AuthError {
  code: 'NO_SESSION' | 'TOKEN_EXPIRED' | 'INVALID_TOKEN' | 'NETWORK_ERROR' | 'UNKNOWN_ERROR' | 'CIRCUIT_BREAKER';
  message: string;
  retryable: boolean;
}

export interface PasswordResetError {
  code: 'USER_NOT_FOUND' | 'INVALID_CODE' | 'CODE_EXPIRED' | 'PASSWORD_POLICY' | 'RATE_LIMITED' | 'UNKNOWN_ERROR';
  message: string;
}

class AuthService {
  private lastTokenInfo: AuthToken | null = null;
  private retryAttempts = 0;
  private maxRetries = 2; // ✅ REDUCED from 3 to 2
  
  // Circuit breaker properties
  private consecutiveFailures = 0;
  private circuitBreakerThreshold = 3; // ✅ REDUCED from 5 to 3
  private circuitBreakerResetTime = 30000; // ✅ REDUCED from 60s to 30s
  private circuitBreakerOpenUntil = 0;
  
  // ✅ NEW: Track if we've seen a NO_SESSION error (don't retry these)
  private hasNoSessionError = false;

  /**
   * Check if circuit breaker is open
   */
  private isCircuitBreakerOpen(): boolean {
    const now = Date.now();
    
    if (this.circuitBreakerOpenUntil > 0 && now > this.circuitBreakerOpenUntil) {
      authLogger.info('Circuit breaker reset after timeout');
      this.resetCircuitBreaker();
      return false;
    }
    
    return this.circuitBreakerOpenUntil > 0;
  }

  /**
   * Record failure and potentially open circuit breaker
   */
  private recordFailure(): void {
    this.consecutiveFailures++;
    
    if (this.consecutiveFailures >= this.circuitBreakerThreshold) {
      this.circuitBreakerOpenUntil = Date.now() + this.circuitBreakerResetTime;
      authLogger.error('Circuit breaker OPENED - too many consecutive auth failures', {
        consecutiveFailures: this.consecutiveFailures,
        resetIn: this.circuitBreakerResetTime / 1000 + ' seconds'
      });
    }
  }

  /**
   * Reset circuit breaker on success
   */
  private resetCircuitBreaker(): void {
    if (this.consecutiveFailures > 0) {
      authLogger.info('Circuit breaker reset - auth working again');
    }
    this.consecutiveFailures = 0;
    this.circuitBreakerOpenUntil = 0;
    this.hasNoSessionError = false; // ✅ NEW: Reset no-session flag
  }

  /**
   * Get fresh authentication token using Amplify's fetchAuthSession()
   * This automatically handles token refresh and ensures we always have valid tokens
   */
  async getToken(): Promise<string> {
    if (this.isCircuitBreakerOpen()) {
      const timeUntilReset = Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
      throw this.createAuthError(
        'CIRCUIT_BREAKER', 
        `Authentication circuit breaker is open. Too many failures. Retry in ${timeUntilReset} seconds.`,
        false // ✅ Not retryable
      );
    }

    // ✅ NEW: If we've already detected no session, don't even try
    if (this.hasNoSessionError) {
      throw this.createAuthError('NO_SESSION', 'No valid authentication session. Please log in.', false);
    }

    try {
      authLogger.debug('Getting fresh token via fetchAuthSession');
      
      const session = await fetchAuthSession();
      
      if (!session.tokens?.idToken) {
        authLogger.error('No ID token in session');
        this.hasNoSessionError = true; // ✅ NEW: Flag this so we stop retrying
        this.recordFailure();
        throw this.createAuthError('NO_SESSION', 'No valid authentication session found', false);
      }

      const idToken = session.tokens.idToken.toString();
      
      if (!idToken || !idToken.startsWith('eyJ')) {
        authLogger.error('Invalid token format received');
        this.recordFailure();
        throw this.createAuthError('INVALID_TOKEN', 'Invalid token format received', false);
      }

      // Parse token payload for debugging and caching
      const payload = this.parseTokenPayload(idToken);
      
      // Verify required custom claims
      if (!payload['custom:tenantId']) {
        authLogger.error('Missing custom:tenantId claim in token');
        this.recordFailure();
        throw this.createAuthError('INVALID_TOKEN', 'Token missing required tenant ID', false);
      }

      // Cache token info for debugging
      this.lastTokenInfo = {
        token: idToken,
        tenantId: payload['custom:tenantId'],
        userRole: payload['custom:role'] || 'Unknown',
        userId: payload.sub,
        expiresAt: (payload.exp || 0) * 1000 // Convert to milliseconds
      };

      authLogger.info('Fresh token obtained', {
        tenantId: this.lastTokenInfo.tenantId,
        userRole: this.lastTokenInfo.userRole,
        userId: this.lastTokenInfo.userId,
        tokenLength: idToken.length,
        expiresAt: new Date(this.lastTokenInfo.expiresAt).toISOString()
      });

      // Reset circuit breaker and retry counter on success
      this.resetCircuitBreaker();
      this.retryAttempts = 0;

      return idToken;

    } catch (error: any) {
      authLogger.error('Failed to get token', undefined, error);
      
      // Don't double-record failures for circuit breaker errors or NO_SESSION
      if (error.code !== 'CIRCUIT_BREAKER' && error.code !== 'NO_SESSION') {
        this.recordFailure();
      }
      
      // Handle different error types
      if (error.name === 'UserUnAuthenticatedException') {
        this.hasNoSessionError = true; // ✅ NEW: Flag this
        throw this.createAuthError('NO_SESSION', 'User is not authenticated. Please log in again.', false);
      }
      
      if (error.message?.includes('Network')) {
        throw this.createAuthError('NETWORK_ERROR', 'Network error while getting auth token', true);
      }

      throw error.code ? error : this.createAuthError('UNKNOWN_ERROR', error.message || 'Failed to get authentication token', false);
    }
  }

  /**
   * Make authenticated HTTP request with automatic token handling
   */
  async makeAuthenticatedRequest(
    url: string, 
    options: RequestInit = {}
  ): Promise<Response> {
    if (this.isCircuitBreakerOpen()) {
      const timeUntilReset = Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
      throw this.createAuthError(
        'CIRCUIT_BREAKER', 
        `Authentication circuit breaker is open. Too many failures. Retry in ${timeUntilReset} seconds.`,
        false
      );
    }

    // ✅ NEW: If we've seen a NO_SESSION error, don't even try to get token
    if (this.hasNoSessionError) {
      throw this.createAuthError('NO_SESSION', 'Please log in again', false);
    }

    const token = await this.getToken();
    
    const authHeaders = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    authLogger.debug('Making authenticated request', { 
      url: url.replace(/https?:\/\/[^\/]+/, ''), // Remove domain for cleaner logs
      method: options.method || 'GET',
      retryAttempt: this.retryAttempts
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers: authHeaders,
      });

      // ✅ IMPROVED: Handle 401 more intelligently
      if (response.status === 401) {
        authLogger.warn('Received 401 Unauthorized', { 
          url: url.replace(/https?:\/\/[^\/]+/, ''),
          retryAttempt: this.retryAttempts + 1,
          maxRetries: this.maxRetries
        });
        
        this.recordFailure();
        
        // ✅ CHANGED: Only retry if we haven't hit the limit AND it's not a NO_SESSION error
        if (this.retryAttempts < this.maxRetries && !this.hasNoSessionError) {
          this.retryAttempts++;
          authLogger.debug('Retrying request with fresh token', { 
            attempt: this.retryAttempts,
            maxRetries: this.maxRetries 
          });
          
          // Clear the cached token and try again
          this.lastTokenInfo = null;
          
          // Force new token and retry
          return this.makeAuthenticatedRequest(url, options);
        }
        
        // ✅ IMPROVED: After retries exhausted, mark as NO_SESSION to stop future attempts
        authLogger.error('Authentication failed after max retries - marking session as invalid', {
          retryAttempts: this.retryAttempts,
          consecutiveFailures: this.consecutiveFailures
        });
        
        this.hasNoSessionError = true; // ✅ NEW: Stop all future attempts
        throw this.createAuthError('NO_SESSION', 'Please log in again', false);
      }

      if (response.status === 403) {
        authLogger.error('Received 403 Forbidden', { 
          url: url.replace(/https?:\/\/[^\/]+/, ''),
          tenantId: this.lastTokenInfo?.tenantId,
          userRole: this.lastTokenInfo?.userRole
        });
        throw this.createAuthError('INVALID_TOKEN', 'Insufficient permissions for this operation', false);
      }

      // Success - reset circuit breaker and retry counter
      this.resetCircuitBreaker();
      this.retryAttempts = 0;
      
      return response;

    } catch (error: any) {
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        authLogger.error('Network error during request', { url: url.replace(/https?:\/\/[^\/]+/, '') });
        throw this.createAuthError('NETWORK_ERROR', 'Network error during API request', true);
      }
      
      throw error;
    }
  }

  /**
   * Get circuit breaker status for debugging
   */
  getCircuitBreakerStatus() {
    return {
      isOpen: this.isCircuitBreakerOpen(),
      consecutiveFailures: this.consecutiveFailures,
      openUntil: this.circuitBreakerOpenUntil,
      timeUntilReset: Math.max(0, Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000)),
      hasNoSessionError: this.hasNoSessionError // ✅ NEW: Expose this for debugging
    };
  }

  /**
   * Force reset circuit breaker (for admin/debugging)
   */
  forceResetCircuitBreaker(): void {
    authLogger.info('Circuit breaker manually reset');
    this.resetCircuitBreaker();
    this.retryAttempts = 0;
  }

  /**
   * Initiate password reset for a user (sends email with code)
   */
  async initiatePasswordReset(email: string): Promise<void> {
    try {
      authLogger.info('Initiating password reset', { email });
      
      await resetPassword({ username: email });
      
      authLogger.info('Password reset email sent successfully', { email });
    } catch (error: any) {
      authLogger.error('Password reset initiation failed', { email }, error);
      throw this.createPasswordResetError(error);
    }
  }

  /**
   * Confirm password reset with code from email
   */
  async confirmPasswordReset(email: string, confirmationCode: string, newPassword: string): Promise<void> {
    try {
      authLogger.info('Confirming password reset', { email });
      
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: newPassword
      });
      
      authLogger.info('Password reset confirmed successfully', { email });
    } catch (error: any) {
      authLogger.error('Password reset confirmation failed', { email }, error);
      throw this.createPasswordResetError(error);
    }
  }

  /**
   * Change password for authenticated user
   */
  async changeUserPassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      authLogger.info('Changing password for authenticated user');
      
      await updatePassword({
        oldPassword: oldPassword,
        newPassword: newPassword
      });
      
      authLogger.info('Password changed successfully');
    } catch (error: any) {
      authLogger.error('Password change failed', undefined, error);
      throw this.createPasswordResetError(error);
    }
  }

  /**
   * Get current token info for debugging (without making API call)
   */
  getLastTokenInfo(): AuthToken | null {
    return this.lastTokenInfo;
  }

  /**
   * Check if we need to refresh token (within 5 minutes of expiry)
   */
  shouldRefreshToken(): boolean {
    if (!this.lastTokenInfo) return true;
    
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return this.lastTokenInfo.expiresAt < fiveMinutesFromNow;
  }

  /**
   * Parse JWT token payload without verification (for debugging)
   */
  private parseTokenPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      authLogger.warn('Could not parse token payload', { error: (error as Error).message });
      return {};
    }
  }

  /**
   * Create standardized auth error
   */
  private createAuthError(code: AuthError['code'], message: string, retryable: boolean = false): AuthError {
    return {
      code,
      message,
      retryable
    } as AuthError;
  }

  /**
   * Create standardized password reset error
   */
  private createPasswordResetError(error: any): PasswordResetError {
    let code: PasswordResetError['code'] = 'UNKNOWN_ERROR';
    let message = error.message || 'An unknown error occurred';

    // Map Cognito error codes to our standardized codes
    switch (error.name) {
      case 'UserNotFoundException':
        code = 'USER_NOT_FOUND';
        message = 'No account found with that email address';
        break;
      case 'CodeMismatchException':
        code = 'INVALID_CODE';
        message = 'Invalid verification code';
        break;
      case 'ExpiredCodeException':
        code = 'CODE_EXPIRED';
        message = 'Verification code has expired. Please request a new one.';
        break;
      case 'InvalidPasswordException':
        code = 'PASSWORD_POLICY';
        message = 'Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, number, and special character.';
        break;
      case 'TooManyRequestsException':
        code = 'RATE_LIMITED';
        message = 'Too many attempts. Please wait before trying again.';
        break;
      case 'LimitExceededException':
        code = 'RATE_LIMITED';
        message = 'Rate limit exceeded. Please wait before trying again.';
        break;
      default:
        // Keep the original message for unknown errors
        break;
    }

    return { code, message };
  }

  /**
   * Clear cached token info (for logout)
   */
  clearTokenCache(): void {
    this.lastTokenInfo = null;
    this.retryAttempts = 0;
    this.hasNoSessionError = false; // ✅ NEW: Reset this flag
    // Don't reset circuit breaker on logout - keep it for protection
    authLogger.debug('Token cache cleared');
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export for convenience
export { AuthService };

// Utility function for components that need quick token access
export async function getAuthToken(): Promise<string> {
  return authService.getToken();
}

// Utility function for components that need authenticated fetch
export async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  return authService.makeAuthenticatedRequest(url, options);
}

// Password reset utility functions
export async function initiatePasswordReset(email: string): Promise<void> {
  return authService.initiatePasswordReset(email);
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  return authService.confirmPasswordReset(email, code, newPassword);
}

export async function updateUserPassword(oldPassword: string, newPassword: string): Promise<void> {
  return authService.changeUserPassword(oldPassword, newPassword);
}

// Circuit breaker utilities
export function getCircuitBreakerStatus() {
  return authService.getCircuitBreakerStatus();
}

export function resetCircuitBreaker() {
  return authService.forceResetCircuitBreaker();
}
